import type { AxiosInstance } from "axios";

import { toast } from "sonner";

import type { Product } from "@/types/products";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";

interface ProductsQueryResponse {
	data: {
		items: Product[];
		meta: {
			totalItems: number;
			itemCount: number;
			itemsPerPage: number;
			totalPages: number;
			currentPage: number;
		};
	};
	message: string;
	isSuccessful: boolean;
}
interface ProductQueryResponse {
	data: Product;
	message: string;
	isSuccessful: boolean;
}

export async function createVendorProduct(
	axios: AxiosInstance,
	product: {
		name: string;
		description: string;
		categoryIds: string;
		price: number | string;
		quantity: number | string;
		currencyId?: string;
		files?: File[];
	}
) {
	if (!product.files?.length)
		throw new Error("At least one product photo is required");
	if (!product.categoryIds)
		throw new Error("Pick a category for the new product");
	if (!product.currencyId)
		throw new Error("Could not load currency. Try again in a moment.");

	const form = new FormData();
	product.files.forEach((file, index) => {
		form.append("files", file, `image${index}.jpg`);
	});
	form.append("name", product.name);
	form.append("description", product.description);
	form.append("price", String(product.price));
	form.append("quantity", String(product.quantity));
	form.append("status", "ACTIVE");
	form.append("categoryIds", product.categoryIds);
	form.append("currencyId", product.currencyId);

	const { data } = await axios.post<ProductQueryResponse>("/product/create_product", form, {
		headers: {
			"Content-Type": "multipart/form-data"
		}
	});
	const created = (data as any)?.data?.id
		? (data as any).data
		: (data as any)?.data?.data ?? data;
	if (!created?.id)
		throw new Error("Product was created but the response had no id");
	return created as Product;
}

async function fetchProducts(axios: AxiosInstance) {
	const response = await axios.get<ProductsQueryResponse>("/product/list_products");

	return response.data.data;
}
async function fetchProduct(axios: AxiosInstance, productId: string) {
	await new Promise(resolve => setTimeout(resolve, 2000));
	const data = await axios.get<ProductQueryResponse>(`/product/view_product/${productId}`);
	return data.data.data;
}
export function useProducts() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["products"],
		queryFn: () => fetchProducts(privateAxios)
	});
}
export function useProduct(productId: string) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["product", productId],
		enabled: !!productId,
		queryFn: () => fetchProduct(privateAxios, productId)
	});
}
export function useDeleteProduct() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["delete-product"],
		mutationFn: async (productId: string) => {
			return await privateAxios.delete(`/product/${productId}`);
		},
		onError: () => toast.error("Failed to delete product"),
		onSuccess: () => toast.success("Product deleted successfully"),
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["products"] });
		}
	});
}
